<template>
  <div class="admin-tab-content">
    <div class="tab-header">
      <h2>👤 Gestión de Características de Usuario</h2>
      <p class="description">Busca y modifica características y permisos de usuarios</p>
    </div>

    <!-- Search Section -->
    <div class="search-section">
      <div class="control-section">
        <label for="search-type">Buscar por:</label>
        <select id="search-type" v-model="searchType" class="admin-select">
          <option value="uid">UID</option>
          <option value="email">Email</option>
        </select>
      </div>

      <div class="control-section">
        <label for="search-query">{{ searchType === 'uid' ? 'UID' : 'Email' }}:</label>
        <div class="search-input-wrapper">
          <input
            type="text"
            id="search-query"
            v-model="searchQuery"
            @keyup.enter="searchUser"
            class="admin-input"
            :placeholder="`Ingrese ${searchType === 'uid' ? 'UID' : 'email'}`"
          />
          <button @click="searchUser" class="btn-primary" :disabled="searching">
            {{ searching ? 'Buscando...' : 'Buscar' }}
          </button>
        </div>
      </div>
    </div>

    <!-- User Info Section -->
    <div v-if="userInfo" class="user-info-section">
      <h3>Información de Usuario</h3>
      <div class="info-card">
        <p><strong>UID:</strong> {{ userInfo.uid }}</p>
        <p><strong>Nombre:</strong> {{ userInfo.name }}</p>
        <p><strong>Email:</strong> {{ userInfo.mail }}</p>
      </div>
    </div>

    <!-- Features Section -->
    <div v-if="userInfo && userFeatures" class="features-section">
      <div class="features-tabs">
        <button
          :class="['tab-btn', { active: activeFeatureTab === 'permissions' }]"
          @click="activeFeatureTab = 'permissions'"
        >
          Permisos
        </button>
        <button
          :class="['tab-btn', { active: activeFeatureTab === 'consumibles' }]"
          @click="activeFeatureTab = 'consumibles'"
        >
          Consumibles
        </button>
      </div>

      <!-- Permissions Tab -->
      <div v-if="activeFeatureTab === 'permissions'" class="feature-tab-content">
        <h4>Permisos del Usuario</h4>
        <div v-if="userFeatures.permissions" class="features-list">
          <div
            v-for="(value, key) in userFeatures.permissions"
            :key="key"
            class="feature-item"
          >
            <span class="feature-key">{{ key }}</span>
            <span :class="['feature-value', value ? 'enabled' : 'disabled']">
              {{ value ? 'Activado' : 'Desactivado' }}
            </span>
          </div>
        </div>
        <div v-else class="no-data">No hay permisos configurados</div>

        <!-- Edit Permission -->
        <div class="edit-section">
          <h5>Editar Permiso</h5>
          <div class="edit-controls">
            <select v-model="selectedPermission" class="admin-select">
              <option value="">Seleccionar permiso</option>
              <option
                v-for="(value, key) in userFeatures.permissions"
                :key="key"
                :value="key"
              >
                {{ key }}
              </option>
            </select>
            <select v-model="permissionValue" class="admin-select">
              <option :value="true">Activado</option>
              <option :value="false">Desactivado</option>
            </select>
            <button
              @click="savePermission"
              class="btn-primary"
              :disabled="!selectedPermission || saving"
            >
              {{ saving ? 'Guardando...' : 'Guardar' }}
            </button>
          </div>
        </div>
      </div>

      <!-- Consumibles Tab -->
      <div v-if="activeFeatureTab === 'consumibles'" class="feature-tab-content">
        <h4>Consumibles del Usuario</h4>
        <div v-if="userFeatures.consumibles" class="features-list">
          <div
            v-for="(value, key) in userFeatures.consumibles"
            :key="key"
            class="feature-item"
          >
            <span class="feature-key">{{ key }}</span>
            <span class="feature-value">{{ value }}</span>
          </div>
        </div>
        <div v-else class="no-data">No hay consumibles configurados</div>

        <!-- Edit Consumible -->
        <div class="edit-section">
          <h5>Editar Consumible</h5>
          <div class="edit-controls">
            <select v-model="selectedConsumible" class="admin-select">
              <option value="">Seleccionar consumible</option>
              <option
                v-for="(value, key) in userFeatures.consumibles"
                :key="key"
                :value="key"
              >
                {{ key }}
              </option>
            </select>
            <input
              type="number"
              v-model.number="consumibleValue"
              class="admin-input"
              min="0"
              placeholder="Valor"
            />
            <select v-model="consumibleOperation" class="admin-select">
              <option value="set">Establecer</option>
              <option value="add">Añadir</option>
              <option value="consume">Consumir</option>
            </select>
            <button
              @click="saveConsumible"
              class="btn-primary"
              :disabled="!selectedConsumible || saving"
            >
              {{ saving ? 'Guardando...' : 'Guardar' }}
            </button>
          </div>
        </div>
      </div>
    </div>

    <div v-if="message" :class="['message-box', messageType]">
      {{ message }}
    </div>
  </div>
</template>

<script>
import axios from 'axios';

export default {
  name: 'UserFeaturesTab',
  data() {
    return {
      searchType: 'uid',
      searchQuery: '',
      searching: false,
      saving: false,
      userInfo: null,
      userFeatures: null,
      activeFeatureTab: 'permissions',
      selectedPermission: '',
      permissionValue: true,
      selectedConsumible: '',
      consumibleValue: 0,
      consumibleOperation: 'set',
      message: '',
      messageType: ''
    };
  },
  methods: {
    async searchUser() {
      if (!this.searchQuery.trim()) {
        this.showMessage('Por favor ingrese un valor de búsqueda', 'error');
        return;
      }

      this.searching = true;
      this.message = '';
      this.userInfo = null;
      this.userFeatures = null;

      try {
        const response = await axios.get(`${process.env.VUE_APP_API}/adminmenu/user-features/lookup`, {
          params: {
            query: this.searchQuery,
            type: this.searchType
          }
        });

        if (response.data.success) {
          this.userInfo = response.data.user;
          this.userFeatures = response.data.features;

          if (!this.userFeatures || (!this.userFeatures.permissions && !this.userFeatures.consumibles)) {
            this.showMessage('Usuario encontrado (sin características configuradas)', 'success');
          }
        } else {
          this.showMessage(response.data.message || 'Usuario no encontrado', 'error');
        }
      } catch (error) {
        this.showMessage('Error al buscar usuario', 'error');
      } finally {
        this.searching = false;
      }
    },
    async savePermission() {
      if (!this.selectedPermission) {
        this.showMessage('Por favor seleccione un permiso', 'error');
        return;
      }

      this.saving = true;
      this.message = '';

      try {
        const response = await axios.post(
          `${process.env.VUE_APP_API}/adminmenu/user-features/update`,
          {
            uid: this.userInfo.uid,
            feature: {
              key: this.selectedPermission,
              value: this.permissionValue,
              type: 'permissions'
            }
          }
        );

        if (response.data.success) {
          // Update local data
          this.userFeatures.permissions[this.selectedPermission] = this.permissionValue;
          this.showMessage('Permiso actualizado correctamente', 'success');
        } else {
          this.showMessage(response.data.message || 'Error al actualizar', 'error');
        }
      } catch (error) {
        this.showMessage('Error al guardar el permiso', 'error');
      } finally {
        this.saving = false;
      }
    },
    async saveConsumible() {
      if (!this.selectedConsumible) {
        this.showMessage('Por favor seleccione un consumible', 'error');
        return;
      }

      if (this.consumibleValue < 0) {
        this.showMessage('El valor debe ser mayor o igual a 0', 'error');
        return;
      }

      this.saving = true;
      this.message = '';

      try {
        const response = await axios.post(
          `${process.env.VUE_APP_API}/adminmenu/user-features/update`,
          {
            uid: this.userInfo.uid,
            feature: {
              key: this.selectedConsumible,
              value: this.consumibleValue,
              type: 'consumibles',
              operation: this.consumibleOperation
            }
          }
        );

        if (response.data.success) {
          // Refresh user data to get updated values
          await this.searchUser();
          this.showMessage('Consumible actualizado correctamente', 'success');
        } else {
          this.showMessage(response.data.message || 'Error al actualizar', 'error');
        }
      } catch (error) {
        this.showMessage('Error al guardar el consumible', 'error');
      } finally {
        this.saving = false;
      }
    },
    showMessage(msg, type) {
      this.message = msg;
      this.messageType = type;

      if (type === 'success') {
        setTimeout(() => {
          this.message = '';
        }, 3000);
      }
    }
  }
};
</script>

<style scoped>
.admin-tab-content {
  padding: 20px;
}

.tab-header h2 {
  color: #3498db;
  font-size: 24px;
  margin-bottom: 10px;
}

.description {
  color: #bbb;
  margin-bottom: 30px;
}

.search-section {
  background: #2c2c2c;
  padding: 20px;
  border-radius: 6px;
  margin-bottom: 20px;
}

.control-section {
  margin-bottom: 15px;
}

.control-section label {
  display: block;
  font-weight: 500;
  margin-bottom: 8px;
  color: #e6e6e6;
}

.search-input-wrapper {
  display: flex;
  gap: 10px;
}

.admin-input {
  background-color: #333;
  border: 1px solid #444;
  border-radius: 4px;
  color: #fff;
  font-size: 14px;
  height: 38px;
  padding: 0 10px;
  flex: 1;
}

.admin-select {
  background-color: #333;
  border: 1px solid #444;
  border-radius: 4px;
  color: #fff;
  font-size: 14px;
  height: 38px;
  padding: 0 10px;
  min-width: 150px;
}

.btn-primary {
  background: #3498db;
  border: none;
  border-radius: 4px;
  color: white;
  cursor: pointer;
  font-size: 14px;
  padding: 10px 20px;
  transition: all 0.2s ease;
  white-space: nowrap;
}

.btn-primary:hover:not(:disabled) {
  background: #2980b9;
  transform: translateY(-1px);
}

.btn-primary:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.user-info-section {
  background: #2c2c2c;
  padding: 20px;
  border-radius: 6px;
  margin-bottom: 20px;
}

.user-info-section h3 {
  color: #3498db;
  margin-bottom: 15px;
}

.info-card {
  background: #333;
  padding: 15px;
  border-radius: 4px;
  border-left: 3px solid #3498db;
}

.info-card p {
  margin: 8px 0;
  color: #e6e6e6;
}

.features-section {
  background: #2c2c2c;
  padding: 20px;
  border-radius: 6px;
}

.features-tabs {
  display: flex;
  gap: 10px;
  margin-bottom: 20px;
  border-bottom: 1px solid #444;
}

.tab-btn {
  background: transparent;
  border: none;
  color: #bbb;
  cursor: pointer;
  font-size: 14px;
  padding: 10px 15px;
  position: relative;
  transition: color 0.2s ease;
}

.tab-btn:hover {
  color: #3498db;
}

.tab-btn.active {
  color: #3498db;
  font-weight: 500;
}

.tab-btn.active::after {
  content: '';
  position: absolute;
  bottom: -1px;
  left: 0;
  width: 100%;
  height: 2px;
  background-color: #3498db;
}

.feature-tab-content h4 {
  color: #e6e6e6;
  margin-bottom: 15px;
}

.features-list {
  margin-bottom: 20px;
}

.feature-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
  background: #333;
  padding: 10px 15px;
  border-radius: 4px;
  margin-bottom: 8px;
}

.feature-key {
  font-weight: 500;
  color: #e6e6e6;
}

.feature-value {
  color: #3498db;
}

.feature-value.enabled {
  color: #2ecc71;
}

.feature-value.disabled {
  color: #e74c3c;
}

.no-data {
  color: #bbb;
  text-align: center;
  padding: 20px;
  font-style: italic;
}

.edit-section {
  background: #333;
  padding: 15px;
  border-radius: 4px;
  margin-top: 20px;
}

.edit-section h5 {
  color: #bbb;
  font-size: 14px;
  margin-bottom: 10px;
}

.edit-controls {
  display: flex;
  gap: 10px;
  flex-wrap: wrap;
}

.message-box {
  background-color: #333;
  border-radius: 4px;
  margin-top: 15px;
  padding: 10px;
  border-left: 3px solid #3498db;
}

.message-box.success {
  border-left-color: #2ecc71;
  color: #2ecc71;
}

.message-box.error {
  border-left-color: #e74c3c;
  color: #e74c3c;
}
</style>
